import { useEffect, useRef } from 'react';
import { ClipboardIcon } from '../../../icons/SvgIcons';
import { EmptyState } from '../../ui/EmptyState/EmptyState';
import Loader from '../../ui/Loader/Loader';
import { AuditLogsTableHeader } from './components/AuditLogsTableHeader/AuditLogsTableHeader';
import { AuditLogsTableRow } from './components/AuditLogsTableRow/AuditLogsTableRow';
import { AuditLogsTableProps } from './AuditLogsTable.types';

export function AuditLogsTable({
  entries,
  loading = false,
  hasMore = false,
  onLoadMore,
  onRowClick,
  sortBy,
  sortOrder,
  onSort,
  showSeverity = false,
  canEditSeverity = false,
  onSeverityUpdated,
}: AuditLogsTableProps) {
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (observerEntries) => {
        if (observerEntries[0].isIntersecting && hasMore && !loading) {
          onLoadMore?.();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, onLoadMore]);

  if (loading && entries.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[230px]">
        <Loader />
      </div>
    );
  }

  if (entries.length === 0 && !loading) {
    return (
      <EmptyState
        icon={<ClipboardIcon />}
        title="No Logs yet"
        description="Activities will appear here once recorded."
      />
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="w-full rounded-lg bg-white shadow-sm border border-solid border-gray-250v2 relative">
        <table className="w-full table-fixed">
          <AuditLogsTableHeader
            showSeverity={showSeverity}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={onSort}
          />
          <tbody>
            {entries.map((entry) => (
              <AuditLogsTableRow
                key={entry.id}
                entry={entry}
                onRowClick={onRowClick}
                showSeverity={showSeverity}
                canEditSeverity={canEditSeverity}
                onSeverityUpdated={onSeverityUpdated}
              />
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div ref={observerTarget} className="flex justify-center py-4">
          {loading && <Loader />}
        </div>
      )}
    </div>
  );
}
